import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { State } from "../reducer";
import { isStaff } from "../utils/access";
import { getAllHistoryUser, getOrderByStatus } from "../api/order.api";

const POLL_MS = 20_000;

export type SidebarBadges = {
  incomingOrders: number;
  myReady: number;
};

const EMPTY: SidebarBadges = { incomingOrders: 0, myReady: 0 };

export function useSidebarBadges(): SidebarBadges {
  const role = useSelector((s: State) => s.user?.role);
  const userId = useSelector((s: State) => s.user?.id);
  const [badges, setBadges] = useState<SidebarBadges>(EMPTY);

  useEffect(() => {
    if (!role || !userId) {
      setBadges(EMPTY);
      return;
    }

    let cancelled = false;
    let timer: number | null = null;
    const staff = isStaff(role);

    const tick = async () => {
      try {
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);

        const [incoming, mine] = await Promise.all([
          staff ? getOrderByStatus("ordered") : Promise.resolve([] as any[]),
          getAllHistoryUser(),
        ]);

        if (cancelled) return;

        const incomingToday = (incoming as any[]).filter(
          (o) => new Date(o.orderDate) >= todayStart
        ).length;
        const myReady = (mine as any[]).filter((o) => o.status === "done").length;

        setBadges({ incomingOrders: incomingToday, myReady });
      } catch {
        // silent — keep last known counts on transient errors
      }
    };

    const start = () => {
      if (timer != null) return;
      tick();
      timer = window.setInterval(tick, POLL_MS);
    };
    const stop = () => {
      if (timer != null) {
        window.clearInterval(timer);
        timer = null;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        if (timer == null) start();
        else tick();
      } else {
        stop();
      }
    };

    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [role, userId]);

  return badges;
}
